'use client';

import React, { useState, useEffect, useMemo } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Table, Tag, Card, Button, Space, Typography, Input } from 'antd';
import { ReloadOutlined, BoxPlotOutlined, SearchOutlined } from '@ant-design/icons';
import axios from 'axios';
import BASE_URL from '../../api';

const { Title, Text } = Typography;

const PickInList = () => {
  const [loading, setLoading] = useState(false);
  const [pickIns, setPickIns] = useState([]);
  const [searchTerm, setSearchTerm] = useState(''); // Search State

  // Fetch data on component mount
  useEffect(() => {
    fetchPickIns();
  }, []);

  const fetchPickIns = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BASE_URL}/api/pickins`);
      const data = res.data.data || [];

      const sortedData = data.sort((a, b) =>
        new Date(b.createddate) - new Date(a.createddate)
      );

      setPickIns(sortedData);
    } catch (err) {
      console.error("Fetch Error:", err);
      toast.error('Failed to load Pick-In records');
    } finally {
      setLoading(false);
    }
  };

  /* ------------------------------------------------------------------ */
  /* SEARCH FILTER LOGIC                                               */
  /* ------------------------------------------------------------------ */
  const filteredData = useMemo(() => {
    const term = searchTerm.toLowerCase().trim();
    if (!term) return pickIns;

    return pickIns.filter((row) => {
      const docketNo = row.docketnumber?.docketNumber?.toLowerCase() || '';
      const consignee = row.docketnumber?.consigneeid?.name?.toLowerCase() || '';
      const pincode = row.docketnumber?.topincode?.pincode?.toString() || '';
      const status = row.status?.toLowerCase() || '';

      return (
        docketNo.includes(term) ||
        consignee.includes(term) ||
        pincode.includes(term) ||
        status.includes(term)
      );
    });
  }, [pickIns, searchTerm]);

  /* ------------------------------------------------------------------ */
  /* TABLE COLUMNS                                                     */
  /* ------------------------------------------------------------------ */
  const columns = [
    {
      title: 'S.No',
      key: 'sno',
      width: 70,
      render: (_, record, index) => {
        // Search Fix: Find index in the original list if searching
        if (searchTerm) {
          return pickIns.findIndex(item => item._id === record._id) + 1;
        }
        return index + 1;
      }
    },
    {
      title: 'Docket Information',
      key: 'docket',
      render: (row) => (
        <Space direction="vertical" size={0}>
          <Text strong>{row.docketnumber?.docketNumber || 'N/A'}</Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            ID: {row.docketnumber?._id?.slice(-6)}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Consignee & Destination',
      key: 'destination',
      render: (row) => (
        <Space direction="vertical" size={0}>
          <Text>{row.docketnumber?.consigneeid?.name || '-'}</Text>
          <Tag color="blue">{row.docketnumber?.topincode?.pincode || "No Pincode"}</Tag>
        </Space>
      ),
    },
    {
      title: 'Package Details',
      key: 'packages',
      render: (row) => (
        <Space>
          <BoxPlotOutlined />
          <Text>{row.docketnumber?.boxConfirmationModel?.length || 0} Boxes</Text>
        </Space>
      ),
    },
    {
      title: 'Condition Remarks',
      dataIndex: 'boxconfirmation',
      key: 'boxconfirmation',
      render: (text) => text || <Text type="secondary">No remarks</Text>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={status === "Arrived at Hub" ? "green" : "orange"}>
          {status?.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Pick-In Date & Time',
      dataIndex: 'createddate',
      key: 'createddate',
      render: (date) => (
        <Text type="secondary">
          {date ? new Date(date).toLocaleString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          }) : '-'}
        </Text>
      ),
    }
  ];

  return (
    <div style={{ padding: '24px', background: '#f5f5f5', minHeight: '100vh' }}>
      <ToastContainer />
      <Card
        bordered={false}
        className="shadow-sm"
        title={
          <Space>
            <BoxPlotOutlined style={{ color: '#1890ff' }} />
            <Title level={4} style={{ margin: 0 }}>Hub Pick-In Inventory</Title>
          </Space>
        }
        extra={
          <Space>
            {/* Search Input added here */}
            <Input
              placeholder="Search Docket, Consignee, Pincode..."
              prefix={<SearchOutlined />}
              allowClear
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ width: 300 }}
            />
            <Button
              type="primary"
              icon={<ReloadOutlined />}
              onClick={fetchPickIns}
              loading={loading}
            >
              Refresh
            </Button>
          </Space>
        }
      >
        <Table
          dataSource={filteredData} // Use filteredData instead of pickIns
          columns={columns}
          rowKey="_id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} items`
          }}
          scroll={{ x: 1000 }}
        />
      </Card>
    </div>
  );
};

export default PickInList;